import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { LayoutDashboard, Bike, ListChecks, LogOut, Calendar, Tag, Hotel, FileSignature, FileText, Users, ShieldCheck, UserCog, UsersRound, CalendarOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandMark from "@/components/BrandMark";

export default function AdminLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isFull = !!user?.is_main_admin;
  const perms = user?.permissions || {};
  const has = (p) => isFull || !!perms[p];

  const allNav = [
    { to: "/admin", label: t("admin.dashboard"), icon: LayoutDashboard, exact: true, testId: "admin-nav-dashboard", show: true },
    { to: "/admin/fleet", label: t("admin.fleet"), icon: Bike, testId: "admin-nav-fleet", show: has("view_fleet") || has("manage_fleet") },
    { to: "/admin/calendar", label: i18n.language === "en" ? "Calendar" : "Calendario", icon: Calendar, testId: "admin-nav-calendar", show: has("view_bookings") || has("manage_bookings") || has("view_fleet") },
    { to: "/admin/bookings", label: t("admin.bookings"), icon: ListChecks, testId: "admin-nav-bookings", show: has("view_bookings") || has("manage_bookings") },
    { to: "/admin/contracts", label: i18n.language === "en" ? "Contracts" : "Contratti", icon: FileSignature, testId: "admin-nav-contracts", show: has("manage_contracts") },
    { to: "/admin/customers", label: i18n.language === "en" ? "Customers" : "Clienti", icon: Users, testId: "admin-nav-customers", show: has("view_customers") || has("manage_customers") },
    { to: "/admin/contract-template", label: i18n.language === "en" ? "Contract Template" : "Modello Contratto", icon: FileText, testId: "admin-nav-template", show: isFull },
    { to: "/admin/privacy-template", label: i18n.language === "en" ? "Privacy GDPR" : "Privacy GDPR", icon: ShieldCheck, testId: "admin-nav-privacy", show: isFull },
    { to: "/admin/promo-codes", label: i18n.language === "en" ? "Promo Codes" : "Codici Promo", icon: Tag, testId: "admin-nav-promo", show: has("manage_promos") },
    { to: "/admin/hotel-conventions", label: i18n.language === "en" ? "Hotel Conv." : "Convenzioni", icon: Hotel, testId: "admin-nav-hotel", show: has("manage_promos") },
    { to: "/admin/business-closures", label: i18n.language === "en" ? "Closures" : "Chiusure", icon: CalendarOff, testId: "admin-nav-closures", show: isFull },
    { to: "/admin/profile", label: i18n.language === "en" ? "My account" : "Mio account", icon: UserCog, testId: "admin-nav-profile", show: true },
    { to: "/admin/team", label: i18n.language === "en" ? "Team" : "Team", icon: UsersRound, testId: "admin-nav-team", show: isFull },
  ];
  const adminNav = allNav.filter((n) => n.show);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col fixed inset-y-0 left-0 border-r border-white/10 bg-zinc-950 px-5 py-6 z-30">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <BrandMark size="md" showWordmark={false} />
            <div className="flex flex-col">
              <span className="font-display font-bold text-base leading-none">Rent Lupi</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t("admin.control_room")}</span>
            </div>
          </div>
          <LanguageSwitcher variant="ghost" />
        </div>

        <nav className="flex flex-col gap-1 overflow-y-auto pr-1 -mr-1">
          {adminNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.exact} data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                    isActive ? "bg-[#FFD700] text-black" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                  }`
                }>
                <Icon className="w-4 h-4" strokeWidth={2} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

      <div className="mt-auto border-t border-white/10 pt-4 space-y-2">
        {isFull && <BlockRecoveryButton lang={i18n.language} />}
        <div className="text-xs text-zinc-500 mb-1">{t("admin.loggato_come")}</div>
          <div className="text-sm font-semibold mb-0.5 truncate text-[#FFD700]" data-testid="admin-user-name">
            {user?.display_name || user?.name || user?.email}
          </div>
          <div className="text-[10px] text-zinc-500 mb-3 truncate">
            {user?.email} · {isFull ? (i18n.language === "en" ? "Full Admin" : "Admin Completo") : (user?.admin_role || "sub-admin")}
          </div>
          <Button variant="outline" size="sm"
                  className="w-full bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200"
                  onClick={async () => { await logout(); navigate("/login"); }}
                  data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
          </Button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-30 w-full backdrop-blur-xl bg-zinc-950/80 border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <BrandMark size="sm" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="ghost" />
          <Button size="sm" variant="ghost" onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-mobile-logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="flex-1 md:ml-64 lg:ml-72">
        <div className="px-5 md:px-8 lg:px-12 py-6 md:py-10">
          <Outlet />
        </div>

        <nav className="md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl bg-zinc-950/90 border-t border-white/10 flex overflow-x-auto no-scrollbar">
          {adminNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.exact}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2 px-3 min-w-[64px] flex-shrink-0 ${isActive ? "text-[#FFD700]" : "text-zinc-500"}`
                }>
                <Icon className="w-4 h-4" />
                <span className="text-[9px] font-medium truncate max-w-[64px]">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </main>
    </div>
  );
}


function BlockRecoveryButton({ lang }) {
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const isEN = lang === "en";
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => {
      api.get("/admin/security/recovery-status").then((r) => setBlocked(!!r.data?.blocked)).catch(() => {});
    });
  }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const api = (await import("@/lib/api")).default;
      if (blocked) {
        await api.post("/admin/security/unblock-recovery");
        setBlocked(false);
      } else {
        await api.post("/admin/security/block-recovery", { duration_hours: 2 });
        setBlocked(true);
      }
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy}
            className={`w-full text-xs font-bold uppercase tracking-wider py-2 rounded-md border transition ${
              blocked ? "bg-red-500/30 border-red-500 text-red-200 animate-pulse"
                      : "bg-red-500/15 border-red-500/40 text-red-300 hover:bg-red-500/25"}`}
            data-testid="block-recovery-button">
      {blocked ? (isEN ? "● RECOVERY BLOCKED" : "● RECUPERO BLOCCATO") : (isEN ? "⛔ Block recovery" : "⛔ Blocca recupero")}
    </button>
  );
}
